export async function withRequestTimeout(run, parentSignal, milliseconds = 10000) {
    const controller = new AbortController();
    let abortReason;
    const abort = () => { abortReason = parentSignal?.reason || new DOMException('Canceled', 'AbortError'); controller.abort(abortReason); };
    parentSignal?.addEventListener('abort', abort, {once: true});
    if (parentSignal?.aborted) abort();
    const timer = setTimeout(() => {
        abortReason = new DOMException('Chat refresh timed out', 'TimeoutError');
        controller.abort(abortReason);
    }, milliseconds);
    let cancel;
    const canceled = new Promise((_, reject) => {
        cancel = () => reject(abortReason || controller.signal.reason || new DOMException('Canceled', 'AbortError'));
        controller.signal.addEventListener('abort', cancel, {once: true});
        if (controller.signal.aborted) cancel();
    });
    try {
        if (controller.signal.aborted) return await canceled;
        return await Promise.race([run(controller.signal), canceled]);
    } catch (error) {
        controller.abort(error);
        throw error;
    } finally {
        clearTimeout(timer);
        parentSignal?.removeEventListener('abort', abort);
        controller.signal.removeEventListener('abort', cancel);
    }
}

// One batch in flight; invalidations arriving during it form one next batch.
export function createRefreshQueue(run, merge, {minIntervalMs = 0, retry = false} = {}) {
    let timer = null, lastStarted = -Infinity, retryAt = 0, failures = 0;
    let pending = [], running = null, scheduled = false, generation = 0;
    function schedule() {
        if (running || scheduled || !pending.length) return;
        scheduled = true;
        const wait = Math.max(0, lastStarted + minIntervalMs - Date.now(), retryAt - Date.now());
        if (wait) timer = setTimeout(flush, wait);
        else queueMicrotask(flush);
    }
    async function flush() {
        scheduled = false;
        timer = null;
        if (running || !pending.length) return;
        const batch = pending;
        pending = [];
        const live = batch.filter(item => !item.signal?.aborted);
        if (!live.length) { schedule(); return; }
        const controller = new AbortController();
        running = controller;
        const epoch = generation;
        lastStarted = Date.now();
        const input = live.map(item => item.input).reduce(merge);
        try {
            const value = await withRequestTimeout(signal => run(input, signal), controller.signal);
            failures = 0; retryAt = 0;
            live.forEach(item => item.resolve(value));
        } catch (error) {
            live.forEach(item => item.reject(error));
            if (retry && epoch === generation && !controller.signal.aborted && error?.name !== 'AbortError'
                && (!error.status || [429, 500, 502, 503, 504].includes(error.status))) {
                const delay = Math.min(15000, 2000 * (2 ** Math.min(failures++, 3)) * (0.8 + Math.random() * 0.4));
                retryAt = Date.now() + Math.max(delay, error.retryAfterMs || 0);
                pending.push({input, signal: live[0].signal, resolve: () => {}, reject: () => {}});
            }
        }
        finally {
            if (epoch === generation) { running = null; schedule(); }
        }
    }
    return {
        request(input, signal) {
            if (signal?.aborted) return Promise.reject(signal.reason || new DOMException('Canceled', 'AbortError'));
            return new Promise((resolve, reject) => {
                const aborted = () => reject(signal.reason || new DOMException('Canceled', 'AbortError'));
                const settle = callback => value => { signal?.removeEventListener('abort', aborted); callback(value); };
                signal?.addEventListener('abort', aborted, {once: true});
                pending.push({input, signal, resolve: settle(resolve), reject: settle(reject)});
                schedule();
            });
        },
        cancel() {
            generation++;
            clearTimeout(timer); timer = null; scheduled = false; retryAt = 0; failures = 0;
            running?.abort();
            running = null;
            pending.splice(0).forEach(item => item.reject(new DOMException('Chat session ended', 'AbortError')));
        },
    };
}
